import { TTrackTypes } from "@/types";
import { getPathwayStyling } from "@/shared/utils/stylingConfigOperations";
import { DEFAULT_STYLING_CONFIG } from "@/types/stylingConfig";

export const TRACK_KEYS = {
  MAIN: "MAIN",
  BK: "BK",
  MINI: "MINI",
  JUNIOR: "JUNIOR",
  INTERMEDIATE: "INTERMEDIATE",
  MEMBERSHIP: "MEMBERSHIP",
  ARCADE: "ARCADE",
} as const;

const TRACK_DISPLAY_NAMES = {
  MAIN: "Main Track",
  BK: "BattleKart",
  MINI: "Mini Track",
  JUNIOR: "Junior Track",
  INTERMEDIATE: "Intermediate Track",
  MEMBERSHIP: "Membership",
  ARCADE: "Arcade Card",
} as const;

// Centralized gradient definitions for consistent theming (defaults; theme config overrides via getTrackGradient)
const TRACK_GRADIENTS = {
  BK: DEFAULT_STYLING_CONFIG.pathways!.bk!.stickySummary!.pillBackground!,
  DEFAULT: DEFAULT_STYLING_CONFIG.pathways!.hk!.stickySummary!.pillBackground!,
} as const;

// Solid colors for accent elements
const TRACK_ACCENT_COLORS = {
  BK: {
    primary: "#FAC618", // Yellow
    secondary: "#EA581A", // Orange
  },
  DEFAULT: {
    primary: "#40BFFF", // Cyan
    secondary: "#FF00FF", // Magenta
  },
} as const;

/**
 * Get the appropriate gradient for a track (theme styling config when present)
 */
export const getTrackGradient = (trackKey: string): string => {
  if (trackKey === TRACK_KEYS.BK) {
    return getPathwayStyling("bk").stickySummary?.pillBackground ?? TRACK_GRADIENTS.BK;
  }
  return getPathwayStyling("hk").stickySummary?.pillBackground ?? TRACK_GRADIENTS.DEFAULT;
};

/**
 * Get accent colors for a track
 */
export const getTrackAccentColors = (trackKey: string) => {
  return trackKey === TRACK_KEYS.BK ? TRACK_ACCENT_COLORS.BK : TRACK_ACCENT_COLORS.DEFAULT;
};

export const getTrackColors = (trackKey: string) => {
  const isBattleKart = trackKey === TRACK_KEYS.BK;
  const isMembership = trackKey === TRACK_KEYS.MEMBERSHIP;

  return {
    background: isBattleKart ? "tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A]" : "tw-bg-[#9d62fe]",
    activeSessionBg: isBattleKart ? "tw-bg-[#EA581A]" : isMembership ? "tw-bg-[#FFA500]" : "tw-bg-[#9d62fe]",
    buttonGradient: isBattleKart
      ? "tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] hover:tw-opacity-90"
      : isMembership
      ? "tw-bg-gradient-to-b tw-from-[#FFD700] tw-to-[#FFA500] hover:tw-opacity-90"
      : "tw-bg-[#9d62fe] hover:tw-opacity-90",
    confirmButton: isBattleKart ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" : "bg-gradient-2",
    cancelButtonText: isBattleKart ? "tw-text-[#EA581A] tw-border-[#EA581A]" : "tw-text-[#9D62FE] tw-border-[#9D62FE]",
    paginationActive: isBattleKart ? "tw-bg-[#EA581A]" : "tw-bg-[#9d62fe]",
  };
};

export const determineTrackType = (item: any, trackSelections: any): { trackType: string; trackKey: string } => {
  const { product } = item.variantsSelectedObject;
  const title = product?.title?.toLowerCase() || "";
  const productType = product?.productType?.toLowerCase() || "";

  // Arcade Cards - check both productType and title with flexible matching
  if (
    productType.includes("arcade") ||
    title.includes("arcade card") ||
    title.includes("arcade game") ||
    (title.includes("arcade") && (title.includes("pre-paid") || title.includes("prepaid")))
  ) {
    return { trackType: TRACK_DISPLAY_NAMES.ARCADE, trackKey: TRACK_KEYS.ARCADE };
  }

  // BattleKart
  if (product?.productType === "BattleKart" || title.includes("battlekart") || title.includes("battle kart")) {
    return { trackType: TRACK_DISPLAY_NAMES.BK, trackKey: TRACK_KEYS.BK };
  }

  // Membership
  if (
    product?.productType === "membership" ||
    title.includes("membership") ||
    title.includes("member") ||
    ((!product?.productType || product?.productType === "") && (title.includes("membership") || title.includes("member")))
  ) {
    return { trackType: TRACK_DISPLAY_NAMES.MEMBERSHIP, trackKey: TRACK_KEYS.MEMBERSHIP };
  }

  // Junior Track
  if (title.includes("junior")) {
    return { trackType: TRACK_DISPLAY_NAMES.JUNIOR, trackKey: TRACK_KEYS.JUNIOR };
  }

  // Intermediate Track
  if (title.includes("intermediate")) {
    return { trackType: TRACK_DISPLAY_NAMES.INTERMEDIATE, trackKey: TRACK_KEYS.INTERMEDIATE };
  }

  // Mini Track
  if (title.includes("mini")) {
    return { trackType: TRACK_DISPLAY_NAMES.MINI, trackKey: TRACK_KEYS.MINI };
  }

  // Check Redux state for HyperKarting products
  const { mainTrackSelection, battleKartSelection, miniTrackSection, juniorTrackSelection, intermediateTrackSelection } = trackSelections;

  // Check BattleKart first (before Main Track to prevent defaulting)
  const isInBattleKart = battleKartSelection?.variantsAndDate?.selectedVariants?.some((v: any) => v.variantsSelectedObject.id === item.variantsSelectedObject.id);
  if (isInBattleKart) {
    return { trackType: TRACK_DISPLAY_NAMES.BK, trackKey: TRACK_KEYS.BK };
  }

  const isInMainTrack = mainTrackSelection?.variantsAndDate?.selectedVariants?.some((v: any) => v.variantsSelectedObject.id === item.variantsSelectedObject.id);
  const isInMiniTrack = miniTrackSection?.variantsAndDate?.selectedVariants?.some((v: any) => v.variantsSelectedObject.id === item.variantsSelectedObject.id);
  const isInJuniorTrack = juniorTrackSelection?.variantsAndDate?.selectedVariants?.some((v: any) => v.variantsSelectedObject.id === item.variantsSelectedObject.id);
  const isInIntermediateTrack = intermediateTrackSelection?.variantsAndDate?.selectedVariants?.some((v: any) => v.variantsSelectedObject.id === item.variantsSelectedObject.id);

  if (isInMainTrack) {
    return { trackType: TRACK_DISPLAY_NAMES.MAIN, trackKey: TRACK_KEYS.MAIN };
  }
  if (isInMiniTrack) {
    return { trackType: TRACK_DISPLAY_NAMES.MINI, trackKey: TRACK_KEYS.MINI };
  }
  if (isInJuniorTrack) {
    return { trackType: TRACK_DISPLAY_NAMES.JUNIOR, trackKey: TRACK_KEYS.JUNIOR };
  }
  if (isInIntermediateTrack) {
    return { trackType: TRACK_DISPLAY_NAMES.INTERMEDIATE, trackKey: TRACK_KEYS.INTERMEDIATE };
  }

  // Default to Main Track
  return { trackType: TRACK_DISPLAY_NAMES.MAIN, trackKey: TRACK_KEYS.MAIN };
};

export const getTrackTypeFromKey = (trackKey: string): TTrackTypes | null => {
  switch (trackKey) {
    case TRACK_KEYS.MAIN:
      return "MAIN_TRACK";
    case TRACK_KEYS.BK:
      return "BATTLE_KART_TRACK";
    case TRACK_KEYS.MINI:
      return "MINI_TRACK";
    case TRACK_KEYS.JUNIOR:
      return "JUNIOR_TRACK";
    case TRACK_KEYS.INTERMEDIATE:
      return "INTERMEDIATE_TRACK";
    case TRACK_KEYS.ARCADE:
      return "ARCADE";
    default:
      return null;
  }
};
